import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; 
import { app } from "../config/firebaseConfig";
import { getAuth, GoogleAuthProvider, signInWithPopup, sendSignInLinkToEmail } from "firebase/auth";
import { checkUserExists, createUserProfile } from '../services/firestoreService';
import resumeDesignLogo from '../images/resumedesign-logo.png';

const auth = getAuth(app);

const Login = () => {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getRedirectDestination = () => {
    if (location.state?.from) return location.state.from;
    const redirectParam = new URLSearchParams(location.search).get('redirect');
    if (redirectParam) {
      return redirectParam.startsWith('/') ? redirectParam : `/${redirectParam}`;
    }
    const returnUrl = localStorage.getItem('returnUrl');
    if (returnUrl) {
      localStorage.removeItem('returnUrl');
      return returnUrl;
    }
    if (localStorage.getItem('selectedPlan')) {
      return '/plans';
    }
    return '/dashboard';
  };

  useEffect(() => {
    if (user) {
      navigate(getRedirectDestination());
    }
    if (location.state?.message) {
      setInfoMessage(location.state.message);
    }
  }, [user, navigate, location]);

  const displayError = (message) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage("");
    }, 6000);
  };

  const handleEmailLinkSignup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      displayError("Please enter a valid email address.");
      return;
    }

    setIsEmailSending(true);
    const actionCodeSettings = {
      url: `${window.location.origin}/finish-sign-up`,
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      setEmailSent(true);
      setInfoMessage(`We've sent a magic sign-in link to ${email}. Click the link to log in instantly!`);
    } catch (error) {
      console.error("Error sending sign-in link: ", error);
      displayError(error.message);
    } finally {
      setIsEmailSending(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    setIsGoogleLoading(true);
    setErrorMessage("");

    try {
      const result = await signInWithPopup(auth, provider);
      const { user: googleUser } = result;
      const userExists = await checkUserExists(googleUser.uid);

      if (!userExists) {
        await createUserProfile(googleUser.uid, googleUser.email, googleUser.displayName, "Free");
      }
      
      navigate(getRedirectDestination());
    } catch (error) {
      console.error("Error signing in with Google:", error);
      if (error.code !== 'auth/popup-closed-by-user') {
        displayError(error.message || "Failed to sign in with Google. Please try again.");
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-purple-50 login-page">
      {/* Left visual column */}
      <div className="w-1/2 flex items-center justify-center p-8 login-col-left">
        <div className="text-left max-w-md">
          <div className='text-center mt-[-30px] mb-8'>
            <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
              Fast, Modern & ATS-Friendly
            </span>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2 tracking-tight">
              Accelerate Your Career 🚀
            </h2>
            <h3 className="text-gray-600 text-sm">
              Build a job-winning, professional resume or CV in just 5 minutes with AI.
            </h3>
          </div>
          
          <div className="mt-4 flex justify-center relative">
            <img 
              src="https://app.cvdesigner.ai/static/media/Professional.ac2547101d1de9c53842.png" 
              alt="Professional Template" 
              className="shadow-xl rounded-xl max-w-[320px] ml-[-200px] border border-white" 
            />
            <img 
              src="https://app.cvdesigner.ai/static/media/Influential.641f11a044e5532b51f3.png" 
              alt="Influential Template" 
              className="absolute shadow-2xl rounded-xl max-w-[320px] ml-[60px] mt-[40px] border-2 border-indigo-100" 
            />
          </div>
        </div>
      </div>

      {/* Right login form column */}
      <div className="w-1/2 bg-white flex flex-col items-center justify-center p-8 sm:p-12 login-col-right shadow-2xl border-l border-gray-100">
        <div className="flex p-2 mb-8 resumedesign-login">
          <img src={resumeDesignLogo} alt="ResumeDesign.AI Logo" className="h-10 object-contain" />
        </div>

        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Sign in to your account</h1>
            <p className="text-sm text-gray-500 mt-1">One-click access to all your resumes and documents</p>
          </div>

          {infoMessage && (
            <div className="p-3.5 mb-5 text-sm text-indigo-800 bg-indigo-50 border border-indigo-100 rounded-xl flex items-start gap-2" role="alert">
              <svg className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{infoMessage}</span>
            </div>
          )}

          {errorMessage && (
            <div className="p-3.5 mb-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2" role="alert">
              <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Primary CTA: 1-Click Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
            className="w-full py-3 px-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-xl shadow-sm hover:shadow transition-all font-semibold flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed text-base"
          >
            {isGoogleLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Signing in with Google...</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5">
                  <path fill="#4285F4" d="M44.5 20H24v8.5h11.8C33.4 33.8 29 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.6 1.1 7.6 2.9l5.8-5.8C34.1 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c10.6 0 19.3-8.1 19.9-18.5H44.5z"/>
                  <path fill="#34A853" d="M24 38c4.7 0 8.6-1.5 11.5-4.1l-5.5-4.1c-1.6 1.1-3.6 1.8-6 1.8-4.6 0-8.5-3.1-9.9-7.4H7.6v4.7C10.4 32.9 16.8 38 24 38z"/>
                  <path fill="#FBBC05" d="M13.9 24c-.4-1.2-.6-2.5-.6-3.8s.2-2.6.6-3.8V11.7H7.6C5.9 15.3 5 19.5 5 24s.9 8.7 2.6 12.3l6.3-4.7z"/>
                  <path fill="#EA4335" d="M24 10c2.6 0 4.9.9 6.7 2.4l5.1-5.1C32.6 5.2 28.5 4 24 4c-4.6 0-8.9 1.4-12.4 4.1l6.3 4.7C19.5 11.4 21.6 10 24 10z"/>
                </svg>
                <span>Continue with Google</span>
              </>
            )}
          </button>

          <div className="flex items-center my-6">
            <div className="flex-grow border-t border-gray-200"></div>
            <span className="mx-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Or continue with email</span>
            <div className="flex-grow border-t border-gray-200"></div>
          </div>

          {emailSent ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
              <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-sm font-bold text-green-900">Check your inbox</h4>
              <p className="text-xs text-green-700 mt-1">
                We sent a secure sign-in link to <strong>{email}</strong>.
              </p>
              <button
                type="button"
                onClick={() => setEmailSent(false)}
                className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleEmailLinkSignup} className="space-y-3">
              <div>
                <input
                  type="email"
                  placeholder="name@example.com"
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button 
                type="submit" 
                disabled={isEmailSending}
                className="w-full py-2.5 px-4 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isEmailSending ? 'Sending magic link...' : 'Email me a sign-in link'}
              </button>
              <p className="text-center text-[11px] text-gray-400 mt-2">
                No password required. Instant, secure passwordless login.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
